import { useCallback, useMemo } from 'react';

import useTracking from 'components/Globals/Analytics';
import { ACTION_CALLBACK_STATUS, ACTION_CALLBACK_STEPS, SECTIONS } from 'components/Globals/Analytics/constants';

import { OUTBOUND_LINK_MEDIUM, OUTBOUND_LINK_TYPES } from 'constants/index';

import { addProtocolIfNeed } from 'utils/common';

import { ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import useAppContext from './useAppContext';
import useNavigate from './useNavigate';

const getSanitizedTargetURL = target => {
  if (!target) {
    return null;
  }

  let url;

  try {
    url = new URL(addProtocolIfNeed(target));
  } catch {
    return null;
  }

  return url.toString();
};

const getTargetURLWithUTMParams = (target, utmParams) => {
  if (!target) {
    return null;
  }

  let url;

  try {
    url = new URL(target);

    Object.entries(utmParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  } catch {
    return null;
  }
};

const useOutboundLinkGenerator = ({ target, type, identifier: overrideIdentifier }) => {
  const navigate = useNavigate();
  const { obRouteContext, isTrustedBot } = useAppContext();
  const track = useTracking();
  const identifier = overrideIdentifier || obRouteContext?.page?.identifier;

  const { outboundLink, outboundLinkWithUTM, outboundLinkWithProxy } = useMemo(() => {
    const UTMParams = {
      utm_source: 'operabase.com',
      utm_medium: OUTBOUND_LINK_MEDIUM.REFERRAL,
      ...(identifier && { utm_campaign: identifier }),
      utm_content: type || OUTBOUND_LINK_TYPES.UNCATEGORIZED,
    };

    const sanitizedTargetURL = getSanitizedTargetURL(target);
    const targetURL = getTargetURLWithUTMParams(sanitizedTargetURL, UTMParams);
    let proxyTargetURL = sanitizedTargetURL;

    if (!isTrustedBot && navigate) {
      proxyTargetURL = navigate.getLinkProps({
        baseRoute: ROUTE_RESERVED_KEYWORDS.redirect,
        queryParams: {
          target: sanitizedTargetURL,
          type,
          identifier,
        },
      })?.url;
    }

    return {
      outboundLink: sanitizedTargetURL,
      outboundLinkWithUTM: targetURL,
      outboundLinkWithProxy: proxyTargetURL,
    };
  }, [target, type, identifier, navigate, isTrustedBot]);

  const trackOutboundLink = useCallback(
    ({ isAutoRedirected = true } = {}) => {
      track.actionCallback({
        section: SECTIONS.OUTBOUND_LINK,
        meta: {
          type,
          id: identifier,
          url: outboundLink,
          step: isAutoRedirected
            ? ACTION_CALLBACK_STEPS.OUTBOUND_LINK_REDIRECTION
            : ACTION_CALLBACK_STEPS.OUTBOUND_LINK_CLICK,
          actionStatus: ACTION_CALLBACK_STATUS.SUCCESS,
        },
      });
    },
    [track, identifier, outboundLink, type],
  );

  const openOutboundLink = useCallback(
    e => {
      if (e) {
        e.preventDefault();
      }

      trackOutboundLink({ isAutoRedirected: false });
      window.open(outboundLinkWithProxy, '_blank');
    },
    [outboundLinkWithProxy, trackOutboundLink],
  );

  return {
    outboundLink,
    outboundLinkWithUTM,
    outboundLinkWithProxy,
    openOutboundLink,
    trackOutboundLink,
  };
};

export default useOutboundLinkGenerator;
